// @ts-nocheck
// TODO: tighten types after migration
import React, { useState, useEffect, useRef } from 'react';
import { fetchPromotions, getPromotionImage } from './promotionService';
import './SidebarAd.css';

/**
 * Unified Ad Component used across the application.
 * Supports different sizes and responsive behavior.
 * 
 * @param {string} variant - Currently used to handle specific sizing logic if needed.
 */
const SidebarAd = ({ variant = 'sidebar', onAvailabilityChange }) => {
  const [promotions, setPromotions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isClosed, setIsClosed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const intervalRef = useRef(null);
  const progressIntervalRef = useRef(null);

  // Detect screen size
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 480);
      setIsTablet(width > 480 && width <= 1024);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Determine target size based on variant and device
  const getTargetSize = () => {
    // Store homepage specific logic: always 320x50
    if (variant === 'store-home') {
      return { w: 320, h: 50 };
    }

    // Blog header or specific requests for 200x200 on mobile
    if (variant === 'blog-header') {
      return isMobile ? { w: 200, h: 200 } : { w: 320, h: 50 };
    }
    
    // Default sidebar behavior: 320x50 for mobile/tablet portrait, 200x200 for desktop
    return (isMobile || (isTablet && window.innerHeight > window.innerWidth)) 
      ? { w: 320, h: 50 } 
      : { w: 200, h: 200 };
  };

  const targetSize = getTargetSize();

  useEffect(() => {
    const loadPromotions = async () => {
      try {
        setLoading(true);
        const allPromotions = await fetchPromotions();
        
        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0);
        
        const validPromotions = allPromotions.filter(promotion => {
          if (!promotion.startDate || !promotion.endDate) return false;
          const endDate = new Date(promotion.endDate);
          endDate.setHours(23, 59, 59, 999);
          return currentDate <= endDate;
        });
        
        // Filter based on available images for the current target size
        const promotionsWithValidImages = validPromotions.filter(promotion => {
          const img = getPromotionImage(promotion, 'ad', targetSize.w, targetSize.h);
          return img && img.url;
        });
        
        if (promotionsWithValidImages.length > 0) {
          setAllPromotions(promotionsWithValidImages);
        } else {
          // Fallback
          const fallbackSize = targetSize.w === 200 ? { w: 320, h: 50 } : { w: 200, h: 200 };
          const fallbackPromotions = validPromotions.filter(promotion => {
            const img = getPromotionImage(promotion, 'ad', fallbackSize.w, fallbackSize.h);
            return img && img.url;
          });
          setPromotions(fallbackPromotions.length > 0 ? fallbackPromotions : []);
        }
      } catch (error) {
        console.error('❌ Ad component error:', error);
        setPromotions([]);
      } finally {
        setLoading(false);
      }
    };

    loadPromotions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, isMobile, isTablet]);

  useEffect(() => {
    if (!onAvailabilityChange) return;
    onAvailabilityChange(!loading && !isClosed && promotions.length > 0);
  }, [loading, isClosed, promotions, onAvailabilityChange]);

  const [allPromotions, setAllPromotions] = useState([]);
  useEffect(() => {
    if (allPromotions.length > 0) {
      setPromotions(allPromotions);
      setCurrentIndex(0);
    }
  }, [allPromotions]);

  useEffect(() => {
    setProgress(0);
  }, [currentIndex]);

  useEffect(() => {
    if (promotions.length <= 1 || isClosed || loading) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
      return;
    }

    if (intervalRef.current) clearInterval(intervalRef.current);
    if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);

    progressIntervalRef.current = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + (100 / (5000 / 50));
        return newProgress >= 100 ? 100 : newProgress;
      });
    }, 50);

    intervalRef.current = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % promotions.length);
    }, 5000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    };
  }, [promotions.length, isClosed, loading]);

  const handleClose = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsClosed(true);
    if (onAvailabilityChange) onAvailabilityChange(false);
  };

  const handleDotClick = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    if (index !== currentIndex) {
      setCurrentIndex(index);
      setProgress(0);
    }
  };

  if (loading || isClosed || !promotions || promotions.length === 0) return null;

  const validIndex = currentIndex >= 0 && currentIndex < promotions.length ? currentIndex : 0;
  const currentPromotion = promotions[validIndex];
  const adImage = getPromotionImage(currentPromotion, 'ad', targetSize.w, targetSize.h);

  if (!adImage || !adImage.url) return null;

  return (
    <div className={`sidebar-ad-container w-full overflow-hidden rounded-[8px] border border-[rgba(0,0,0,0.1)] bg-white shadow-[0_2px_4px_rgba(0,0,0,0.1)] ${variant === 'blog-header' ? 'blog-header-ad-variant' : ''}`}>
      <div className="sidebar-ad-top relative flex items-center justify-between bg-bg py-[0.15rem] px-3">
        <div className="sidebar-ad-left z-[1] flex flex-[0_0_auto] items-center gap-2">
          <span className="sidebar-ad-label font-lora text-[0.9rem] font-bold text-[#333]">Ads</span>
        </div>
        {promotions.length > 1 && (
          <div className="sidebar-ad-dots absolute left-1/2 top-1/2 z-[1] flex h-full -translate-x-1/2 -translate-y-1/2 items-center justify-center gap-[0.3rem]">
            {promotions.map((_, index) => (
              <button
                key={index}
                className={`sidebar-ad-dot relative cursor-pointer overflow-hidden border-none bg-[#ccc] p-0 transition-all duration-300 ease-in-out ${index === currentIndex ? 'active h-[6px] w-[20px] rounded-[3px] bg-[#999] hover:bg-[#888]' : 'h-[6px] w-[6px] rounded-full hover:bg-[#aaa]'}`}
                onClick={(e) => handleDotClick(e, index)}
                aria-label={`Go to ad ${index + 1}`}
              >
                <span 
                  className="sidebar-ad-dot-progress absolute left-0 top-0 h-full rounded-[3px] bg-[#47b5ff] transition-[width] duration-75 ease-linear"
                  style={{ 
                    width: index === currentIndex ? `${progress}%` : '0%' 
                  }}
                />
              </button>
            ))}
          </div>
        )}
        <button 
          className="sidebar-ad-close z-[2] flex h-6 w-6 cursor-pointer items-center justify-center border-none bg-none p-0 text-[1.5rem] leading-none text-[#666] transition-colors duration-200 ease-in-out hover:text-black focus:rounded-[2px] focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-[#47b5ff]"
          onClick={handleClose}
          aria-label="Close advertisement"
        >
          ×
        </button>
      </div>
      <div className="sidebar-ad-bottom flex items-center justify-center bg-white p-2">
        <a
          href={currentPromotion.promotionLink}
          target="_blank"
          rel="noopener noreferrer"
          className="sidebar-ad-link inline-block no-underline transition-opacity duration-200 ease-in-out hover:opacity-90"
        >
          <img
            src={adImage.url}
            alt={`${currentPromotion.companyName} - ${currentPromotion.productName}`}
            className={`sidebar-ad-image block max-w-full rounded object-contain ${targetSize.w === 200 ? 'sidebar-ad-image-desktop h-[200px] w-[200px] max-w-[200px]' : 'sidebar-ad-image-mobile h-[50px] w-[320px] max-w-[320px]'}`}
            width={targetSize.w}
            height={targetSize.h}
          />
        </a>
      </div>
    </div>
  );
};

export default SidebarAd;

